<template>
  <simple-dialog ref="dlg" :title="title" width="650" :close-on-save="false" @ok="save">
    <template v-slot:content>
      <p class="text-body-2 text-medium-emphasis mb-3">
        Enter comma-separated ITS IDs and/or select organizations. Members matching either list cannot view the form.
      </p>
      <v-textarea
        label="Excluded ITS IDs"
        v-model="localExclusions.excludedItsIds"
        rows="2"
        placeholder="e.g. 12345678, 87654321"
        class="mb-3"
      />
      <v-autocomplete
        label="Excluded organizations"
        v-model="localExclusions.excludedOrganizationIds"
        :items="organizations"
        item-title="name"
        item-value="organizationId"
        multiple
        chips
        closable-chips
        class="mb-3"
      />
      <v-text-field
        label="Exclusion message (optional)"
        v-model="localExclusions.exclusionReason"
        placeholder="Shown to excluded members"
      />
    </template>
  </simple-dialog>
</template>

<script setup>
import { ref, watch, onMounted, inject } from "vue";
import { getAllOrganizations } from "@/services/utilitiesService";
import { updateSurveyExclusions } from "@/services/surveyService";
import {
  emptySurveyExclusions,
  exclusionsFromApi,
  exclusionsToPayload,
} from "../utils/surveyExclusionUtils";

const props = defineProps({
  modelValue: { type: Object, default: () => emptySurveyExclusions() },
  title: { type: String, default: "Survey Exclusions" },
  surveyId: { type: Number, default: null },
});

const emit = defineEmits(["update:modelValue", "saved"]);

const loader = inject("loader");
const snackbar = inject("snackBar");

const dlg = ref(null);
const localExclusions = ref(emptySurveyExclusions());
const organizations = ref([]);

watch(
  () => props.modelValue,
  (val) => {
    localExclusions.value = { ...emptySurveyExclusions(), ...(val || {}) };
  },
  { immediate: true, deep: true }
);

onMounted(async () => {
  organizations.value = await getAllOrganizations().catch(() => []);
});

function show() {
  localExclusions.value = { ...emptySurveyExclusions(), ...(props.modelValue || {}) };
  dlg.value.show();
}

async function save() {
  const value = { ...localExclusions.value };
  emit("update:modelValue", value);

  if (!props.surveyId) {
    dlg.value.hide();
    return;
  }

  loader.show();
  try {
    const result = await updateSurveyExclusions(props.surveyId, exclusionsToPayload(value));
    emit("update:modelValue", exclusionsFromApi(result));
    emit("saved");
    snackbar.success("Exclusions saved");
    dlg.value.hide();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

defineExpose({ show });
</script>
